<template>
    <div class="p-6 space-y-4">
        <!-- Warning Sections -->
        <div class="space-y-4">
            <div class="bg-red-50 border border-red-200 rounded-lg p-4">
                <div class="flex items-start gap-3">
                    <ExclamationTriangleIcon class="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                        <h3 class="font-semibold text-red-800 mb-2">This action cannot be undone</h3>
                        <p class="text-red-700 text-sm">
                            Deleting your account will permanently remove all your data, including:
                        </p>
                        <ul class="text-red-700 text-sm mt-2 space-y-1">
                            <li>• Personal information and settings</li>
                            <li>• Exposure reports and scan history</li>
                            <li>• Privacy preferences and opt-outs</li>
                            <li>• Account credentials and access</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div class="flex items-start gap-3">
                    <InformationCircleIcon class="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div>
                        <h3 class="font-semibold text-yellow-800 mb-1">Important Notice</h3>
                        <p class="text-yellow-700 text-sm">
                            This action is irreversible. Once your account is deleted, you will lose access to all Privacy Bee services and your data cannot be recovered.
                        </p>
                    </div>
                </div>
            </div>

            <div class="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div class="flex items-start gap-3">
                    <CheckCircleIcon class="h-5 w-5 text-gray-600 mt-0.5 flex-shrink-0" />
                    <div>
                        <h3 class="font-semibold text-gray-800 mb-1">Confirmation Required</h3>
                        <p class="text-gray-700 text-sm">
                            To confirm deletion, please type <strong>"DELETE"</strong> in the field below.
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Confirmation Input -->
        <div class="space-y-2">
            <label for="delete-confirmation" class="block text-sm font-medium text-gray-700">
                Type "DELETE" to confirm
            </label>
            <PrimeInputText
                id="delete-confirmation"
                v-model="deleteConfirmation"
                placeholder="Type DELETE to confirm"
                :class="{ 'border-red-500': deleteConfirmation && deleteConfirmation !== 'DELETE' }"
                :disabled="isDeleting"
            />
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-end gap-3 pt-4">
            <PrimeButton
                label="Cancel"
                variant="outlined"
                @click="$emit('closeDialog')"
                :disabled="isDeleting"
            />
            <PrimeButton
                label="Delete Account"
                class="bg-red-600 hover:bg-red-700 text-white"
                @click="confirmDelete"
                :disabled="deleteConfirmation !== 'DELETE' || isDeleting"
                :loading="isDeleting"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/vue/24/outline';
import { ref } from 'vue';
import { usePbApi } from '~/composables/api';
import { useAuthStore } from '~/stores/auth';

const auth = useAuthStore();

// Reactive variables
const deleteConfirmation = ref('');
const isDeleting = ref(false);

// Methods
const confirmDelete = async () => {
    if (deleteConfirmation.value !== 'DELETE') {
        return;
    }

    isDeleting.value = true;

    try {
        const { status, message } = await usePbApi('me', {
            method: 'DELETE'
        });

        if (status === 204) {
            // Account deleted successfully
            useClientToast().success('Account deleted successfully');
            
            // Clear auth state and redirect to login
            await auth.logout();
            await navigateTo('/auth/login');
        } else {
            throw new Error(message || 'Failed to delete account');
        }
    } catch (error: any) {
        console.error('Delete account error:', error);
        useClientToast().error(error.message || 'Failed to delete account. Please try again.');
    } finally {
        isDeleting.value = false;
    }
};

defineEmits<{
    closeDialog: []
}>();
</script> 